import { useCallback, useState } from 'react'
import type { JSX } from 'react'

import {
  Button,
  Dialog,
  Flex,
  Form,
  Input,
  Label,
  Notice,
  Select,
  Text,
  toast,
} from '@buffer-mono/popcorn'

import { useOrganizationId } from '~publish/contexts/AccountContext'
import { validateInvitationName } from '~publish/helpers/validateInvitationName'

import { validateEmail } from '../../../shared/validation/email'
import { useInviteTeamMembers } from '../../hooks'
import {
  COLLABORATION_ROLE_LABELS,
  COLLABORATION_ROLES,
} from '../../utils/collaborationRoles'
import type { CollaborationRole } from '../../utils/collaborationRoles'

import { ChannelAccessField } from './ChannelAccessField'
import styles from './RolesInviteMemberDialog.module.css'

const DEFAULT_ROLE: CollaborationRole = 'creator'

function isCollaborationRole(value: string): value is CollaborationRole {
  return (COLLABORATION_ROLES as string[]).includes(value)
}

interface RolesInviteMemberDialogProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  onClose: () => void
}

/**
 * Invite dialog for organizations on the Roles model. Sends the chosen
 * `collaborationRole` and the selected `channelIds`; an Admin sends none,
 * since their access to every channel is granted on acceptance (CORE-8045).
 * Custom permissions follow in their own ticket.
 *
 * Deliberately independent of the legacy `InviteMemberDialog`, which goes
 * away with the legacy permission model.
 */
export const RolesInviteMemberDialog = ({
  isOpen,
  onOpenChange,
  onClose,
}: RolesInviteMemberDialogProps): JSX.Element => {
  const organizationId = useOrganizationId()

  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState<CollaborationRole>(DEFAULT_ROLE)
  const [channelIds, setChannelIds] = useState<string[]>([])
  const [emailError, setEmailError] = useState('')
  const [nameError, setNameError] = useState('')
  const [submitError, setSubmitError] = useState('')
  const [hasSubmitted, setHasSubmitted] = useState(false)

  const { inviteTeamMember, loading: inviting } = useInviteTeamMembers()

  const handleEmailBlur = useCallback(() => {
    setEmailError(email.trim() === '' ? '' : (validateEmail(email) ?? ''))
  }, [email])

  const handleNameBlur = useCallback(() => {
    setNameError(validateInvitationName(name) ?? '')
  }, [name])

  const resetForm = useCallback(() => {
    setEmail('')
    setName('')
    setRole(DEFAULT_ROLE)
    setChannelIds([])
    setEmailError('')
    setNameError('')
    setSubmitError('')
    setHasSubmitted(false)
  }, [])

  const handleClose = useCallback(() => {
    resetForm()
    onClose()
  }, [resetForm, onClose])

  // Every dismissal (Cancel, Escape, backdrop) goes through `handleClose` so
  // the form resets. Passing `onOpenChange` straight to Dialog would skip the
  // reset on Escape and backdrop.
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        onOpenChange(true)
      } else {
        handleClose()
      }
    },
    [onOpenChange, handleClose],
  )

  const handleSubmit = async (): Promise<void> => {
    if (!organizationId) return

    setSubmitError('')
    setHasSubmitted(true)

    if (email.trim() === '' || name.trim() === '') {
      return
    }

    const emailProblem = validateEmail(email)
    if (emailProblem) {
      setEmailError(emailProblem)
      return
    }

    // The server rejects the same names (INC-442). Checking here turns a
    // failed mutation into inline feedback on the field that caused it.
    const nameProblem = validateInvitationName(name)
    if (nameProblem) {
      setNameError(nameProblem)
      return
    }

    const result = await inviteTeamMember({
      organizationId,
      email,
      name,
      collaborationRole: role,
      channelIds: role === 'admin' ? undefined : channelIds,
      channelAssignments: [],
    })

    if (result.success) {
      toast.success('Team member invited successfully')
      handleClose()
    } else {
      setSubmitError(result.error ?? 'Failed to invite team member')
    }
  }

  // "Required" only shows after a submit attempt, so an untouched form does
  // not open with errors.
  const emailMessage =
    hasSubmitted && email.trim() === '' ? 'Email is required' : emailError
  const nameMessage =
    hasSubmitted && name.trim() === '' ? 'Name is required' : nameError

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Content size="medium">
        <Dialog.Header>
          <Dialog.Title>Invite Team Member</Dialog.Title>
        </Dialog.Header>
        <Dialog.Body className={styles.body}>
          <Flex direction="column" align="stretch" gap="md" fullWidth>
            {submitError && <Notice variant="error">{submitError}</Notice>}

            <Form.Field name="invite-email" className={styles.field}>
              <Form.Label>Email</Form.Label>
              <Form.Control>
                <Input
                  type="email"
                  size="large"
                  placeholder="email@example.com"
                  value={email}
                  onChange={(e): void => {
                    setEmail(e.target.value)
                    setEmailError('')
                    setSubmitError('')
                  }}
                  onBlur={handleEmailBlur}
                  autoComplete="off"
                  data-1p-ignore
                  data-lpignore="true"
                  data-form-type="other"
                  aria-invalid={emailMessage !== ''}
                  aria-describedby={
                    emailMessage ? 'invite-email-error' : undefined
                  }
                />
              </Form.Control>
              {emailMessage && (
                <Text id="invite-email-error" color="critical" size="sm">
                  {emailMessage}
                </Text>
              )}
            </Form.Field>

            <Form.Field name="invite-name" className={styles.field}>
              <Form.Label>Name</Form.Label>
              <Form.Control>
                <Input
                  type="text"
                  size="large"
                  placeholder="Team member name"
                  value={name}
                  onChange={(e): void => {
                    setName(e.target.value)
                    setNameError('')
                    setSubmitError('')
                  }}
                  onBlur={handleNameBlur}
                  autoComplete="off"
                  data-1p-ignore
                  data-lpignore="true"
                  data-form-type="other"
                  aria-invalid={nameMessage !== ''}
                  aria-describedby={
                    nameMessage ? 'invite-name-error' : undefined
                  }
                />
              </Form.Control>
              {nameMessage && (
                <Text id="invite-name-error" color="critical" size="sm">
                  {nameMessage}
                </Text>
              )}
            </Form.Field>

            <Flex
              align="center"
              justify="between"
              gap="md"
              fullWidth
              className={styles.roleRow}
            >
              <Flex direction="column" gap="3xs">
                <Label htmlFor="invite-role">Role</Label>
                <Text color="subtle" size="sm">
                  What they can do across the team
                </Text>
              </Flex>
              <Select
                value={role}
                onValueChange={(value): void => {
                  if (!isCollaborationRole(value)) return
                  setRole(value)
                  // Admins get every channel, so a selection made for another
                  // role is dropped rather than carried over.
                  if (value === 'admin') setChannelIds([])
                }}
              >
                <Select.Trigger
                  id="invite-role"
                  size="large"
                  className={styles.fieldControl}
                />
                <Select.Content align="end">
                  {COLLABORATION_ROLES.map((value) => (
                    <Select.Item key={value} value={value}>
                      {COLLABORATION_ROLE_LABELS[value]}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select>
            </Flex>

            <ChannelAccessField
              role={role}
              value={channelIds}
              onChange={setChannelIds}
            />
          </Flex>
        </Dialog.Body>
        <Dialog.Footer>
          <Button variant="tertiary" size="large" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="large"
            onClick={handleSubmit}
            disabled={inviting}
            loading={inviting}
          >
            Send Invite
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}
